import { DeleteOutlined, LinkOutlined, WarningFilled } from "@ant-design/icons"
import { Flex, Space, Table, Tag, Tooltip, Typography } from "antd"
import type { ItemType } from "antd/es/menu/interface"
import { type ColumnsType } from "antd/es/table"
import { type BaseType } from "antd/es/typography/Base"
import { type Dayjs } from "dayjs"
import React from "react"

import Icon from "../../../../../../../Icon"
import { RowActionsMenu } from "../../../../../../../components/EntityTable/RowActionsMenu.tsx"
import { empty } from "../../../../../../../constants/placeholders"
import { budgetFormat } from "../../../../../../../utils/number/budget-format.ts"
import { DATE_FORMAT } from "../../../../../../../utils/date/date.ts"
import { tableGuideAnchor } from "../../../../../../../utils/table-guide-anchor.ts"
import {
  type Attachment,
  AttachmentModels
} from "../../../../../../Album/types/attachment.types.ts"
import { AttachmentsController } from "../../../../../components/AttachmentsController.tsx"
import { type MilestoneScheduleRisk } from "../../../../../redux/project-milestones/project-milestones.types.ts"
import { type calculateEffectiveSchedule } from "../../../../utils/milestone-effective-schedule.ts"
import type { EffectiveScheduleEntry } from "../../../../utils/milestone-effective-schedule.ts"
import { isUnscheduledMilestone } from "../../../../utils/milestone-effort.ts"
import { milestoneVarianceDays } from "../../../../utils/milestone-schedule-state.ts"
import { type MilestoneDependency } from "../../../../../types/project-milestone-dependency.types.ts"
import { MilestoneRiskTag } from "./MilestoneRiskTag.tsx"
import { type ProjectMilestone } from "../../../../../types/project-phases.types.tsx"
import { MilestoneStatusTag } from "./MilestoneStatusTag.tsx"
import { type MilestoneStatuses } from "../../../../constants/milestone-statuses.ts"
import {
  PROJECT_ENTITY_CATEGORIES_MAP,
  type ProjectEntityCategories
} from "../../../../constants/project-entity-category.ts"
import { MilestoneFinancialCell } from "./MilestoneFinancialCell.tsx"

const { Text, Paragraph } = Typography

type Translate = (key: string, options?: Record<string, unknown>) => string

interface GetColumnsColumnsArgs {
  t: Translate
  currentUserId: string
  //
  deleteAllowed?: boolean
  editAllowed?: boolean
  uploadFileAllowed?: boolean
  deleteFileAllowed?: boolean
  deleteForeignFileAllowed?: boolean
  //
  isLocked: boolean
  projectId: string
  /** The same dependency-aware schedule the Gantt renders from — a Planned
   * Start/End that would otherwise violate a dependency shows its
   * recalculated date here too, so the two views never disagree. */
  effectiveSchedule?: ReturnType<typeof calculateEffectiveSchedule>
  userFormat?: string | null
  /** Names the predecessor a dependency-dated milestone follows. */
  milestoneNames?: Record<string, string>
  /** The project's dependency graph: a milestone something leads into is
   * never Unscheduled, the same rule the Gantt applies. */
  dependencies?: MilestoneDependency[]
  /** The account's today and date parsing, for Variance. Without them the
   * cell shows the server's figure. */
  today?: Dayjs
  parseDate?: (value: unknown) => Dayjs | null
  onSelectRow: (record: ProjectMilestone, action: string) => void
  contractValue?: number | null
  contractExpenses?: number | null
}

const renderPlannedDate = (
  side: "start" | "end",
  value: string,
  record: ProjectMilestone,
  effectiveSchedule: ReturnType<typeof calculateEffectiveSchedule> | undefined,
  userFormat: string | null | undefined,
  t: Translate,
  hasPredecessor: boolean,
  milestoneNames?: Record<string, string>
) => {
  const entry: EffectiveScheduleEntry | undefined = effectiveSchedule?.get(
    record.id
  )

  if (record.dates_from_dependencies && entry?.isGenerated) {
    const date = side === "start" ? entry.start : entry.end
    const source = entry.shiftReason
      ? milestoneNames?.[entry.shiftReason.sourceMilestoneId]
      : undefined
    return (
      <Space size={4}>
        <Text>{date.format(userFormat || DATE_FORMAT)}</Text>
        <Tooltip
          title={
            source
              ? t("projectMilestones.tooltips.fromDependency", { name: source })
              : t("projectMilestones.tooltips.fromDependencies")
          }
        >
          <Tag bordered={false} color="processing" className="m-0">
            {t("projectMilestones.cells.auto")}
          </Tag>
        </Tooltip>
      </Space>
    )
  }

  if (!value && !entry && isUnscheduledMilestone(record, hasPredecessor)) {
    if (side === "end") return empty
    return (
      <Tooltip title={t("projectMilestones.tooltips.unscheduled")}>
        <Tag bordered={false} className="m-0">
          {t("projectMilestones.cells.unscheduled")}
        </Tag>
      </Tooltip>
    )
  }

  if (!value) {
    if (!entry?.isGenerated) return empty
    const generatedDate = side === "start" ? entry.start : entry.end
    return (
      <Space size={4}>
        <Text type="secondary">
          {generatedDate.format(userFormat || DATE_FORMAT)}
        </Text>
        <Tooltip title={t("projectMilestones.tooltips.autoScheduled")}>
          <LinkOutlined className="text-secondary" />
        </Tooltip>
      </Space>
    )
  }

  if (!entry?.isShifted) return value

  const effectiveDate = side === "start" ? entry.start : entry.end
  const displayValue = effectiveDate.format(userFormat || DATE_FORMAT)

  return (
    <Space size={4}>
      <Text>{displayValue}</Text>
      <Tooltip title={t("projectMilestones.tooltips.shifted", { date: value })}>
        <WarningFilled className="text-warning" />
      </Tooltip>
    </Space>
  )
}

// Days ahead (+) or behind (-) the planned end; `null` when there is no
// planned end to compare against. 0 is a real figure — finished on the day —
// so callers test `=== null`, never truthiness.
const varianceOf = (
  record: ProjectMilestone,
  today: Dayjs | undefined,
  parseDate: ((value: unknown) => Dayjs | null) | undefined
): number | null => {
  if (today && parseDate) return milestoneVarianceDays(record, today, parseDate)
  const server = record?.variance
  if (server === null || server === undefined || String(server).trim() === "") {
    return null
  }
  const days = Number(server)
  return Number.isFinite(days) ? days : null
}

// The row-number column's key. The inline-edit row reads it to know where the
// table's own leading cells end, so the two must name the same column.
export const ORDER_COLUMN_KEY = "order"

// The columns the inline edit row has an editor for, by key. Everything the
// table renders before the first of these (selection, grip, chevron, #) passes
// through the edit row untouched.
export const MILESTONE_EDITABLE_COLUMN_KEYS = [
  "name",
  "milestone_category",
  "status",
  "schedule_risk",
  "description",
  "receivables",
  "payables",
  "start_date",
  "end_date",
  "duration_days",
  "actual_end_date",
  "variance",
  "dependencies",
  "attachments"
] as const

export const getColumns = ({
  t,
  currentUserId,
  projectId,
  isLocked,
  //
  deleteAllowed,
  editAllowed,
  uploadFileAllowed,
  deleteFileAllowed,
  deleteForeignFileAllowed,
  //
  effectiveSchedule,
  userFormat,
  milestoneNames,
  dependencies = [],
  today,
  parseDate,
  onSelectRow,
  contractValue,
  contractExpenses
}: GetColumnsColumnsArgs): ColumnsType<ProjectMilestone> => [
  Table.EXPAND_COLUMN,
  {
    title: t("projectMilestones.columns.number"),
    dataIndex: "order",
    key: ORDER_COLUMN_KEY,
    width: 40,
    align: "center",
    // render: (order: number) => order
    render: (_order: number, _record: ProjectMilestone, index: number) =>
      index + 1
  },
  {
    title: t("projectMilestones.columns.name"),
    dataIndex: "name",
    key: "name",
    width: 220,
    render: (name: string) => {
      return (
        <div className={"max-w-56 w-fit relative"}>
          <Text ellipsis={{ tooltip: name }}>{name}</Text>
        </div>
      )
    }
  },
  {
    title: t("projectMilestones.columns.type"),
    dataIndex: "milestone_category",
    key: "milestone_category",
    width: 165,
    render: (category: ProjectEntityCategories, record) => {
      const data = PROJECT_ENTITY_CATEGORIES_MAP?.[category]

      return <Tag style={data?.style}>{record?.milestone_category_display}</Tag>
    }
  },
  {
    title: t("projectMilestones.columns.status"),
    dataIndex: "status",
    key: "status",
    // Wide enough for the inline-edit select this column holds. Declared
    // narrower, the select pushes the column out and shifts every column to
    // its right the moment a row opens for edit.
    width: 130,
    // A stable anchor for the Learn EZBuild Milestones deep-dive tour to
    // spotlight this column header directly — inert for every real user.
    onHeaderCell: () => tableGuideAnchor("milestone-col-status"),
    render: (status: MilestoneStatuses, record) => (
      <MilestoneStatusTag status={status} label={record?.status_display} />
    )
  },
  {
    title: t("projectMilestones.columns.risk"),
    dataIndex: "schedule_risk",
    key: "schedule_risk",
    width: 110,
    onHeaderCell: () => tableGuideAnchor("milestone-col-risk"),
    render: (risk?: MilestoneScheduleRisk) => <MilestoneRiskTag risk={risk} />
  },
  {
    title: t("projectMilestones.columns.description"),
    dataIndex: "description",
    key: "description",
    width: 260,
    onCell: () => ({ style: { minWidth: 200, maxWidth: 260 } }),
    render: (description: string) => (
      <Paragraph
        className={"mb-0"}
        ellipsis={{ rows: 3, tooltip: description }}
      >
        {description || empty}
      </Paragraph>
    )
  },
  {
    title: t("projectMilestones.columns.receivables"),
    key: "receivables",
    width: 190,
    align: "center",
    onHeaderCell: () => tableGuideAnchor("milestone-col-receivables"),
    onCell: () => ({ style: { whiteSpace: "nowrap" } }),
    render: (_, record) => (
      <MilestoneFinancialCell
        settled={record?.actual_received}
        planned={record?.planned_receivable}
        committed={record?.committed_receivable}
        contractLimit={contractValue}
        contractLabel={t("projectMilestones.labels.contractValue")}
      />
    )
  },
  {
    title: t("projectMilestones.columns.payables"),
    key: "payables",
    width: 190,
    align: "center",
    onHeaderCell: () => tableGuideAnchor("milestone-col-payables"),
    onCell: () => ({ style: { whiteSpace: "nowrap" } }),
    render: (_, record) => (
      <MilestoneFinancialCell
        settled={record?.actual_paid}
        planned={record?.planned_payable}
        committed={record?.committed_payable}
        contractLimit={contractExpenses}
        contractLabel={t("projectMilestones.labels.contractExpenses")}
      />
    )
  },
  {
    title: t("projectMilestones.columns.plannedStart"),
    dataIndex: "start_date",
    key: "start_date",
    width: 135,
    align: "center",
    onHeaderCell: () => tableGuideAnchor("milestone-col-dates"),
    render: (value: string, record) =>
      renderPlannedDate(
        "start",
        value,
        record,
        effectiveSchedule,
        userFormat,
        t,
        dependencies.some((dep) => dep.target_milestone === record.id),
        milestoneNames
      )
  },
  {
    title: t("projectMilestones.columns.plannedEnd"),
    dataIndex: "end_date",
    key: "end_date",
    width: 135,
    align: "center",
    render: (value: string, record) =>
      renderPlannedDate(
        "end",
        value,
        record,
        effectiveSchedule,
        userFormat,
        t,
        dependencies.some((dep) => dep.target_milestone === record.id),
        milestoneNames
      )
  },
  {
    title: t("projectMilestones.columns.effort"),
    dataIndex: "duration_days",
    key: "duration_days",
    width: 110,
    align: "center",
    render: (value?: number | null) => (value ? value : empty)
  },
  {
    title: t("projectMilestones.columns.actualEnd"),
    dataIndex: "actual_end_date",
    key: "actual_end_date",
    width: 145,
    align: "center",
    render: (value: string, record) => {
      const variance = varianceOf(record, today, parseDate)
      const isLate = variance !== null && variance < 0

      return (
        <Space>
          <Text>{value || empty}</Text>
          {isLate && (
            <Tooltip title={t("projectMilestones.tooltips.late")}>
              <WarningFilled className={"text-warning"} />
            </Tooltip>
          )}
        </Space>
      )
    }
  },
  {
    title: t("projectMilestones.columns.variance"),
    dataIndex: "variance",
    key: "variance",
    width: 80,
    align: "center",
    render: (_: number | null, record) => {
      const value = varianceOf(record, today, parseDate)
      if (value === null) return empty

      const settings: Record<
        number,
        {
          simbol: string
          color: BaseType | undefined
        }
      > = {
        [-1]: {
          simbol: "",
          color: "danger"
        },
        [0]: {
          simbol: "",
          color: undefined
        },
        [1]: {
          simbol: "+",
          color: "success"
        }
      }

      const settingsData = settings?.[Math.sign(value)]

      return (
        <Tag>
          <Text strong type={settingsData?.color}>
            {settingsData?.simbol}
            {value}d
          </Text>
        </Tag>
      )
    }
  },
  {
    title: t("projectMilestones.columns.dependencies"),
    key: "dependencies",
    width: 90,
    align: "center",
    onHeaderCell: () => tableGuideAnchor("milestone-col-dependencies"),
    render: (_, record) => {
      const count = record?.dependency_count ?? 0
      if (!count) return <Text type="secondary">{empty}</Text>
      return (
        <Typography.Link
          onClick={() => onSelectRow(record, "manage_dependencies")}
          className="inline-flex items-center gap-1"
          aria-label={t("projectMilestones.cells.dependencyCount", { count })}
        >
          <LinkOutlined /> {count}
        </Typography.Link>
      )
    }
  },
  {
    title: t("projectMilestones.columns.evidence"),
    key: "attachments",
    dataIndex: "attachments",
    width: 80,
    align: "center",
    onHeaderCell: () => tableGuideAnchor("milestone-col-evidence"),
    render: (attachments: Attachment[], record) => (
      <AttachmentsController
        currentUserId={currentUserId}
        // addAllowed={uploadFileAllowed || editAllowed}
        addAllowed={editAllowed}
        deleteAllowed={deleteFileAllowed}
        deleteForeignAllowed={deleteForeignFileAllowed}
        project_id={projectId}
        model_type={AttachmentModels.MILESTONE}
        object_id={record?.id}
        attachments={attachments}
      />
    )
  },
  {
    title: t("projectMilestones.columns.actions"),
    width: 50,
    align: "center",
    fixed: "right",
    hidden: !deleteAllowed && !editAllowed,
    render: (_, record) => (
      <Flex gap={"small"} justify={"center"} className={"w-20 mx-auto"}>
        <RowActionsMenu
          disabled={isLocked}
          items={
            [
              {
                key: "edit_row",
                label: t("projectMilestones.actions.edit"),
                hidden: !editAllowed,
                icon: <Icon type={"Edit"} />,
                onClick: () => onSelectRow(record, "edit_row")
              },
              {
                key: "manage_dependencies",
                label: t("projectMilestones.actions.dependencies"),
                hidden: !editAllowed,
                icon: <LinkOutlined />,
                onClick: () => onSelectRow(record, "manage_dependencies")
              },
              {
                key: "delete_row",
                label: t("projectMilestones.actions.delete"),
                hidden: !deleteAllowed,
                icon: <DeleteOutlined />,
                danger: true,
                onClick: () => onSelectRow(record, "delete_row")
              }
            ] as Array<ItemType & { hidden?: boolean }>
          }
        />
      </Flex>
    )
  }
]
